/**
 * Zod schemas for the condition tree used by form rules and component rules.
 *
 * Schemas are defined here so both the form-rules NL translator
 * (`src/modules/admin/api/formRulesService.ts`, arriving later) and the
 * component-rules layer (`src/components/shared/configurable/`, arriving
 * later) can import them without depending on either module's internals.
 *
 * The shape mirrors the TypeScript types in `../types.ts`:
 *
 * - {@link simpleConditionSchema} validates a {@link SimpleCondition} leaf.
 * - {@link conditionGroupSchema} validates a {@link ConditionGroup} wrapper
 *   with recursive `conditions` via {@link https://zod.dev/ z.lazy}.
 * - {@link fieldConditionSchema} is the public union consumers use — it
 *   accepts either shape and narrows accordingly.
 * - {@link fieldConditionInputSchema} accepts either a single
 *   {@link FieldCondition} or an array of conditions, matching the shape
 *   `FieldConfig.conditions` and `SectionConfig.conditions` now accept.
 *
 * TODO (phase 3+): the server-side translator in Origami will need to
 * mirror these schemas. Keeping the shape narrow here makes that
 * mirroring a mechanical translation.
 */

import { z } from 'zod';

import type {
  ConditionEffect,
  ConditionGroup,
  FieldCondition,
  FieldTypeOptions,
  SimpleCondition,
} from '../types';

// ---------------------------------------------------------------------------
// Primitive pieces
// ---------------------------------------------------------------------------

/**
 * The 11 condition operators supported by the shared evaluator.
 * Must stay in sync with {@link ConditionOperator} in
 * `src/components/integrated/shared/conditionEvaluator.ts`.
 */
export const conditionOperatorSchema = z.enum([
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'greaterThan',
  'lessThan',
  'isEmpty',
  'isNotEmpty',
  'in',
  'notIn',
  'matches',
]);

/**
 * Permissive schema for a condition's `effect` object. We don't introspect
 * the per-field options here — those are validated downstream when the
 * form config is merged. Every property is optional; the caller decides
 * which ones apply to their layer (form rules / component rules).
 */
export const conditionEffectSchema: z.ZodType<ConditionEffect> = z.object({
  visible: z.boolean().optional(),
  required: z.boolean().optional(),
  disabled: z.boolean().optional(),
  setValue: z.unknown().optional(),
  // `FieldTypeOptions` is a large discriminated union; we accept any
  // object here and defer type-specific validation to the field layer.
  options: z
    .custom<FieldTypeOptions>((v) => typeof v === 'object' && v !== null)
    .optional(),
});

// ---------------------------------------------------------------------------
// SimpleCondition
// ---------------------------------------------------------------------------

/**
 * Zod schema for a {@link SimpleCondition} leaf. Field name is a
 * non-empty string; operator is one of the 11 supported values; value is
 * unconstrained (unknown) because operator-value type checks are
 * delegated to the runtime evaluator; effect is the permissive shape
 * defined above.
 */
export const simpleConditionSchema: z.ZodType<SimpleCondition> = z.object({
  when: z.string().min(1),
  operator: conditionOperatorSchema,
  value: z.unknown(),
  effect: conditionEffectSchema,
});

// ---------------------------------------------------------------------------
// ConditionGroup (recursive via z.lazy)
// ---------------------------------------------------------------------------

/**
 * Zod schema for a {@link ConditionGroup}. The `conditions` array can
 * contain either {@link SimpleCondition} leaves or further nested
 * {@link ConditionGroup} instances, so the inner entries reference the
 * public union via {@link z.lazy} to avoid a circular reference at module
 * load time.
 *
 * Component rules will re-export this schema under a namespaced name once
 * the shared configurable layer lands.
 */
export const conditionGroupSchema: z.ZodType<ConditionGroup> = z.lazy(() =>
  z.object({
    operator: z.enum(['AND', 'OR']),
    conditions: z.array(fieldConditionSchema),
  }),
);

// ---------------------------------------------------------------------------
// FieldCondition union and input shapes
// ---------------------------------------------------------------------------

/**
 * Zod schema for a {@link FieldCondition} — the public union of
 * {@link SimpleCondition} and {@link ConditionGroup}. Used anywhere a
 * condition can be either a leaf or a group.
 */
export const fieldConditionSchema: z.ZodType<FieldCondition> = z.lazy(() =>
  z.union([simpleConditionSchema, conditionGroupSchema]),
);

/**
 * Zod schema for the polymorphic condition shape accepted by
 * `FieldConfig.conditions` and `SectionConfig.conditions`: either a single
 * condition (leaf or group) or the legacy array form (implicit AND).
 *
 * This is the schema the NL translator and the component-rules validator
 * apply to arbitrary user-supplied condition payloads.
 */
export const fieldConditionInputSchema: z.ZodType<
  FieldCondition | FieldCondition[]
> = z.union([fieldConditionSchema, z.array(fieldConditionSchema)]);
